import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { isEqual } from 'lodash-es';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { Vdd } from '@vi-iot/client';

import { GTMService, UIService } from '@services';
import {
  RemoteDiagnosticsControlUnit,
  RemoteDiagnosticsSnapshotsStore,
  RemoteDiagnosticsStore,
  RemoteDiagnosticsVddListParameter,
} from '@store';

import { TranslateService } from '@ngx-translate/core';
import { VDDParameterViewValue } from '../../../extended-parameters/diagnostics-constants';
import { RemoteDiagnosticsUiService } from '../../../extended-parameters/remote-diagnostics-ui.service';
import { DiagnosticsHelpers } from '../../../extended-parameters/diagnostics-helpers';
import { Option } from '../../../../../../../projects/ui/components/dropdown-autocomplete/dropdown-autocomplete.component';

@Component({
  selector: 'app-snapshot-vdd-selection',
  templateUrl: './snapshot-vdd-selection.component.html',
  styleUrls: ['./snapshot-vdd-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnapshotVddSelectionComponent implements OnInit, OnDestroy {
  currentControlUnit: RemoteDiagnosticsControlUnit;
  availableParametersExceptSelected: VDDParameterViewValue[] = [];

  @Input() gatewaySerial: string;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public remoteDiagnosticsStore: RemoteDiagnosticsStore,
    public remoteDiagnosticsUiService: RemoteDiagnosticsUiService,
    public uiService: UIService,
    private changeDetectorRef: ChangeDetectorRef,
    private gtmService: GTMService,
    private remoteDiagnosticsSnapshotsStore: RemoteDiagnosticsSnapshotsStore,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.remoteDiagnosticsSnapshotsStore.getAvailableParametersExceptSelected$
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.ngUnsubscribe$))
      .subscribe((parameters: RemoteDiagnosticsVddListParameter[]) => {
        this.availableParametersExceptSelected = parameters
          ? DiagnosticsHelpers.mapVDDListToDisplay(parameters)
          : [];
        this.availableParametersExceptSelected.map((parameter) => {
          parameter.viewValue = this.translate.instant(parameter.value);
        });
        this.changeDetectorRef.detectChanges();
      });

    this.remoteDiagnosticsSnapshotsStore.getSelectedControlUnit$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((selectedControlUnit) => {
        this.currentControlUnit = selectedControlUnit;
        this.changeDetectorRef.detectChanges();
      });
  }

  addParameter(value: Option): void {
    const vdd: Vdd = {
      key: value.viewValueThree,
      logicalLink: this.currentControlUnit.value,
    };

    this.remoteDiagnosticsSnapshotsStore.addParameter(vdd);

    this.remoteDiagnosticsSnapshotsStore.triggerReadVddListValuesForSnapshot(
      this.gatewaySerial,
      vdd
    );
    this.gtmService.track('onSelect', `Paramater name: ${vdd.key}`, 'diagnosticsPage');
  }

  onControlUnitOptionChange(selectedControlUnit: RemoteDiagnosticsControlUnit): void {
    this.remoteDiagnosticsSnapshotsStore.selectControlUnit(selectedControlUnit);
    this.remoteDiagnosticsStore.openLogicalLink(this.gatewaySerial, selectedControlUnit.value);
  }

  compareControlUnits(
    firstControlUnit: RemoteDiagnosticsControlUnit,
    secondControlUnit: RemoteDiagnosticsControlUnit
  ): boolean {
    return firstControlUnit?.value === secondControlUnit?.value;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
